<template>
  <div class="all-users-container">
    <PageHeader 
      pageName="Gestion des utilisateurs"
      pageTitle="Gestion des utilisateurs"
      pageSubtitle="Gérez les utilisateurs de votre plateforme et leur accès."
    />

 
    <div class="users-stats">
      <div class="stats-card">
        <div class="circle-icon">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>
        <div class="stats-info">
          <div class="stats-label">Utilisateurs</div>
          <div class="stats-value">
            <span class="number">{{ formatNumber(totalUsers) }}</span>
          </div>
        </div>
        <button class="refresh-stats" @click="refreshGlobalStats" title="Rafraîchir les statistiques">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M23 4v6h-6"></path>
            <path d="M1 20v-6h6"></path>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
          </svg>
        </button>
      </div>
      
      <div class="stats-card">
        <div class="circle-icon chatbots">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <rect x="3" y="11" width="18" height="10" rx="2"></rect>
            <circle cx="12" cy="5" r="2"></circle>
            <path d="M12 7v4"></path>
            <line x1="8" y1="16" x2="8.01" y2="16"></line>
            <line x1="16" y1="16" x2="16.01" y2="16"></line>
          </svg>
        </div>
        <div class="stats-info">
          <div class="stats-label">Chatbots</div>
          <div class="stats-value">
            <span class="number">{{ formatNumber(totalChatbots) }}</span>
          </div>
        </div>
        <button class="refresh-stats" @click="refreshGlobalStats" title="Rafraîchir les statistiques">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M23 4v6h-6"></path>
            <path d="M1 20v-6h6"></path>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
          </svg>
        </button>
      </div>
      
      <div class="stats-card">
        <div class="circle-icon discussions">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        <div class="stats-info">
          <div class="stats-label">Interactions</div>
          <div class="stats-value">
            <span class="number">{{ formatNumber(totalDiscussions) }}</span>
          </div>
        </div>
        <button class="refresh-stats" @click="refreshGlobalStats" title="Rafraîchir les statistiques">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M23 4v6h-6"></path>
            <path d="M1 20v-6h6"></path>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
          </svg>
        </button>
      </div>
    </div>

    <div class="table-controls">
      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Rechercher un utilisateur..." 
          @input="debounceSearch"
        />
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </div>
      <div class="table-actions" style="display: none;">
        <button class="filter-button" @click="toggleFilterMenu">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"></path>
          </svg>
          Filtres
          <span v-if="activeFilters.length" class="filter-count">{{ activeFilters.length }}</span>
        </button>
        <div v-if="showFilterMenu" class="filter-menu">
          <div class="filter-section">
            <h4>Statut</h4>
            <label class="filter-option">
              <input type="checkbox" v-model="filters.active" @change="applyFilters">
              Utilisateurs actifs
            </label>
            <label class="filter-option">
              <input type="checkbox" v-model="filters.inactive" @change="applyFilters">
              Utilisateurs inactifs
            </label>
          </div>
          <div class="filter-section">
            <h4>Interactions</h4>
            <label class="filter-option">
              <input type="checkbox" v-model="filters.hasInteractions" @change="applyFilters">
              Avec interactions
            </label>
            <label class="filter-option">
              <input type="checkbox" v-model="filters.noInteractions" @change="applyFilters">
              Sans interactions
            </label>
          </div>
          <div class="filter-actions">
            <button class="clear-filters" @click="clearFilters">Effacer les filtres</button>
          </div>
        </div>
      </div>
    </div>

    <div class="users-list-container" ref="scrollContainer">
      <div v-if="loading && users.length === 0" class="loading-container">
        <div class="loader"></div>
        <p>Chargement des utilisateurs...</p>
      </div>

      <div v-else-if="users.length === 0 && !loading" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        <p>Aucun utilisateur trouvé</p>
        <button class="action-button" @click="refreshUsers">
          Réessayer
        </button>
      </div>

      <div v-else class="users-content-wrapper">
        <!-- Desktop Table View -->
        <div class="users-table desktop-only">
          <table>
          <thead>
            <tr>
              <th class="user-cell">Utilisateur</th>
              <th class="user-cell" @click="toggleChatbotSort">
                Chatbot
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'sort-active': chatbotSort !== 'none' }">
                  <path v-if="chatbotSort === 'none'" d="M6 9l6 6 6-6"/>
                  <path v-else-if="chatbotSort === 'asc'" d="M12 19l-6-6h12l-6 6z"/>
                  <path v-else d="M12 5l6 6H6l6-6z"/>
                </svg>
              </th>
              <th class="user-cell" @click="toggleInteractionsSort">
                Interactions
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'sort-active': interactionsSort !== 'none' }">
                  <path v-if="interactionsSort === 'none'" d="M6 9l6 6 6-6"/>
                  <path v-else-if="interactionsSort === 'asc'" d="M12 19l-6-6h12l-6 6z"/>
                  <path v-else d="M12 5l6 6H6l6-6z"/>
                </svg>
              </th>
              <th class="activity-header" @click="toggleActivitySort">
                Dernière activité
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'sort-active': activitySort !== 'none' }">
                  <path v-if="activitySort === 'none'" d="M6 9l6 6 6-6"/>
                  <path v-else-if="activitySort === 'asc'" d="M12 19l-6-6h12l-6 6z"/>
                  <path v-else d="M12 5l6 6H6l6-6z"/>
                </svg>
              </th>
              <th>Actions</th>
              <th class="actions-cell"></th>
            </tr>
          </thead>
          <tbody ref="tableBody">
            <tr v-for="(user, index) in users" :key="user.id || index">
              <td class="user-cell">
                
                 <div class="user-info">
                  <div class="user-avatar">
                    <img :src="getAvatarUrl(user.name)" alt="User avatar">
                    <span class="status-indicator" :class="{ active: user.isActive }"></span>
                    
                  </div>
                  <div class="user-details">
                    <div class="user-name">{{ user.name }}</div>
                    <div class="user-email">{{ user.email || formatPhoneNumber(user.phone) }}</div>
                  </div>
                </div>
              </td>
              <td>
                <div class="access-tags">
                  <span class="access-tag data" @click="redirectToChatbotPage(user.idbot)">
                    {{ user.bot }}
                  </span>
                </div>
              </td>
              <td class="interactions-cell">
                <div class="interactions-count">
                  {{ formatNumber(user.discussions) }}
                </div>
              </td>
              <td>{{ formatTimeAgo(user.lastActivity) }}</td>
              <td class="action-icons">
                <div class="action-icon-container" title="Diffuser un message">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </div>
                <div class="action-icon-container" title="Ouvrir la conversation" @click="openConversation(user)">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div class="action-icon-container" title="Ouvrir WhatsApp" @click="openWhatsApp(user.phone)">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                  </svg>
                </div>
              </td>
              <td class="actions-cell">
                <div class="actions-dropdown">
                  <button class="table-action-button" @click="toggleActionMenu(index)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="1"></circle>
                      <circle cx="19" cy="12" r="1"></circle>
                      <circle cx="5" cy="12" r="1"></circle>
                    </svg>
                  </button>
                  <div v-if="activeActionMenu === index" class="action-menu">
                    <button @click="openConversation(user)" class="action-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      Voir détails
                    </button>
                     
                  </div>
                </div>
              </td>
            </tr>
            <tr ref="sentinelRow" v-if="hasMoreUsers" style="height: 5px; background: transparent; border: none;">
               <td colspan="6" style="padding: 0; border: none;"></td>
            </tr>
          </tbody>
        </table>
        </div>

        <!-- Mobile Cards View -->
        <div class="mobile-users-grid mobile-only">
           <UserCard 
             v-for="(user, index) in users" 
             :key="user.id || index" 
             :user="user"
             @open-conversation="openConversation"
             @click="openConversation"
           />
        </div>
         
        <div class="load-more-container">
            <button 
              v-if="hasMoreUsers && !loading" 
              class="load-more-button" 
              @click="loadMoreUsers"
            >
              Charger plus d'utilisateurs
            </button>
            
            <div v-if="loading" class="loading-more">
              <div class="loader"></div>
              <p>Chargement de plus d'utilisateurs...</p>
            </div>

            <div v-if="!hasMoreUsers && users.length > 0 && !loading" class="end-message">
              Fin de la liste des utilisateurs
            </div>
        </div>

        <div class="pagination-container">
          <div class="pagination-info">
            <div class="users-per-page">
              <select v-model="selectedLimit" @change="handleLimitChange">
                <option value="10">10</option>
                <option value="20" selected>20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="all">Tous</option>
              </select>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, watch, computed } from 'vue';
import { apiBaseUrl } from '@/config';
import axios from 'axios';

import PageHeader from '@/components/PageHeader.vue';
import UserCard from '@/components/UserCard.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'AllUsersPage',
  components: {
    PageHeader,
    UserCard
  },
  setup() {
    const users = ref([]);
    const originalUsers = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const limit = ref("20");
    const selectedLimit = ref("20");
    const hasMoreUsers = ref(true);
    const scrollContainer = ref(null);
    const tableBody = ref(null); // Reference pour le scroll interne
    const searchQuery = ref('');
    const searchTimeout = ref(null);
    const chatbotSort = ref('none');
    const activeActionMenu = ref(null);
    const sentinelRow = ref(null);
    let observer = null;
    const activitySort = ref('none');
    const interactionsSort = ref('none');
    const showFilterMenu = ref(false);
    const filters = ref({
      active: false,
      inactive: false,
      hasInteractions: false,
      hasInteractions: false,
      noInteractions: false
    });
    
    // Mobile Check
    const isMobile = ref(false);
    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768;
    };
    
    onMounted(() => {
        window.addEventListener('resize', checkMobile);
        checkMobile();
    });
    
    onUnmounted(() => {
        window.removeEventListener('resize', checkMobile);
    });

    // Watcher optimisé pour limit
    watch(limit, (newValue) => {
      if (newValue && newValue !== selectedLimit.value) {
        selectedLimit.value = newValue;
      }
    }, { immediate: false });
    
    // Statistiques globales de l'API
    const globalStats = ref({
      chatbotCount: 0,
      userCount: 0,
      discussionCount: 0
    });
    
    // Variables dérivées pour l'affichage
    const totalUsers = ref(0);
    const totalDiscussions = ref(0);
    const totalChatbots = ref(0);
    
    const statsLoading = ref(false);
    const statsError = ref(null);
    const statsLoaded = ref(false);

    const router = useRouter();

    // Computed property pour les filtres actifs
    const activeFilters = computed(() => {
      return Object.entries(filters.value)
        .filter(([_, value]) => value)
        .map(([key]) => key);
    });

    // Fonction pour basculer le menu de filtres
    const toggleFilterMenu = () => {
      showFilterMenu.value = !showFilterMenu.value;
    };

    // Fonction pour appliquer les filtres
    const applyFilters = () => {
      
      
      // Filtrer les utilisateurs selon les critères sélectionnés
      const filteredUsers = originalUsers.value.filter(user => {
        // Filtre par statut
        if (filters.value.active && !user.isActive) return false;
        if (filters.value.inactive && user.isActive) return false;
        
        // Filtre par interactions
        const hasInteractions = (user.discussions || 0) > 0;
        if (filters.value.hasInteractions && !hasInteractions) return false;
        if (filters.value.noInteractions && hasInteractions) return false;
        
        return true;
      });
      
      // Mettre à jour la liste des utilisateurs
      users.value = filteredUsers;
    };

    // Fonction pour effacer tous les filtres
    const clearFilters = () => {
      Object.keys(filters.value).forEach(key => {
        filters.value[key] = false;
      });
      users.value = [...originalUsers.value];
    };

    // Fermer les menus quand on clique ailleurs
    const handleGlobalClick = (event) => {
      if (!event.target.closest('.actions-dropdown')) {
        activeActionMenu.value = null;
      }
      if (!event.target.closest('.table-actions')) {
        showFilterMenu.value = false;
      }
    };

    // Gestionnaire de scroll natif
    const handleTableScroll = (event) => {
       const el = event.target;
       const scrollTop = el.scrollTop;
       const scrollHeight = el.scrollHeight;
       const clientHeight = el.clientHeight;
       const threshold = 100; // Charger 100px avant la fin
       
       if (scrollTop + clientHeight >= scrollHeight - threshold) {
          if (!loading.value && hasMoreUsers.value) {
             loadMoreUsers();
          }
       }
    };

    // Surveiller les changements de tableBody pour attacher l'écouteur
    watch(tableBody, (newVal, oldVal) => {
       if (oldVal) {
          oldVal.removeEventListener('scroll', handleTableScroll);
       }
       if (newVal) {
          newVal.addEventListener('scroll', handleTableScroll);
       }
    });

    // Ajouter l'écouteur de clic global
    onMounted(() => {
      document.addEventListener('click', handleGlobalClick);
      loadGlobalStats(); 
      loadUsers(true);
    });

    // Nettoyer l'écouteur de clic et l'observer
    onUnmounted(() => {
      document.removeEventListener('click', handleGlobalClick);
      if (observer) {
        observer.disconnect();
      }
    });

    // Fonction pour charger les utilisateurs depuis l'API
    const loadUsers = async (reset = false) => {
      if (loading.value) return;
      
      loading.value = true;
      
      
      try {
        // Récupérer le token depuis le localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          consoleError('❌ Données utilisateur non trouvées');
          loading.value = false;
          return;
        }

        const userObj = JSON.parse(userData);
        if (!userObj.data?.token) {
          consoleError('❌ Token non trouvé');
          loading.value = false;
          return;
        }

        if (reset) {
          page.value = 1;
          users.value = [];
        }
        
        // Construire l'URL avec les paramètres de pagination et de recherche
        let url = `${apiBaseUrl}/get/strong/users`;
        let params = {
          page: page.value,
          limit: selectedLimit.value === 'all' ? 999999 : parseInt(selectedLimit.value)
        };
        
        // Ajouter le paramètre de recherche uniquement si une recherche est en cours
        if (searchQuery.value && searchQuery.value.trim() !== '') {
          params.search = searchQuery.value.trim();
          
        }
        
        
        
        const response = await axios({
          method: 'get',
          url: url,
          params: params,
          headers: {
            'Authorization': `Bearer ${userObj.data.token}`
          }
        });
        
        const responseData = response.data;
        
        
        
        // Traitement du nouveau format de réponse (objet avec message, users et pagination)
        if (responseData && responseData.users && Array.isArray(responseData.users)) {
          const usersData = responseData.users;
          
          
          // Obtenir les informations de pagination
          const pagination = responseData.pagination || {};
          
          
          // Ne PAS recalculer les statistiques globales pour éviter d'écraser celles de l'API
          // On utilise les statistiques locales seulement si les globales n'ont pas été chargées
          if (reset && !statsLoaded.value) {
            
            let uniqueBots = new Set();
            let totalDisc = 0;
            
            usersData.forEach(user => {
              if (user.idbot) uniqueBots.add(user.idbot);
              if (user.total_discussions) totalDisc += user.total_discussions;
            });
            
            totalUsers.value = usersData.length;
            totalChatbots.value = uniqueBots.size;
            totalDiscussions.value = totalDisc;
          }
          
          // Mapper les données pour correspondre à notre structure
          const formattedUsers = usersData.map(user => {
            
            const formattedUser = {
              id: user.id_dialog,
              name: user.user_full_name || 'Sans nom',
              phone:  user.tel ,
              phonebot: user.telbot ,
              email: user.email || '',
              bot: user.bot_name || 'Non spécifié',
              idbot: user.idbot || null,
              iduser: user.iduser,
              discussions: user.total_discussions || 0,
              lastActivity: user.created_at || new Date().toISOString(),
              isActive: user.is_active || true,
              profileImage: user.user_image_profile || ''
            };
            return formattedUser;
          });
          
          
          
          // Mettre à jour la liste des utilisateurs
          if (reset) {
            users.value = formattedUsers;
            originalUsers.value = formattedUsers;
          } else {
            users.value = [...users.value, ...formattedUsers];
            originalUsers.value = [...originalUsers.value, ...formattedUsers];
          }
          
          // Incrémenter la page APRÈS un appel API réussi
          page.value++;
          
          
          // Vérifier s'il y a plus d'utilisateurs à charger
          // Si le serveur renvoie moins d'éléments que la limite, c'est qu'il n'y en a plus
          const hasMore = usersData.length >= parseInt(selectedLimit.value);
          hasMoreUsers.value = hasMore;
          
          
        } else {
          consoleWarn('⚠️ Format de réponse inattendu:', responseData);
          hasMoreUsers.value = false;
        }
        
      } catch (error) {
        consoleError('❌ Erreur lors du chargement des utilisateurs:', error.response?.data || error.message || error);
        
        if (error.response) {
          consoleError('❌ Statut de l\'erreur:', error.response.status);
          
          if (error.response.status === 401) {
            consoleError('❌ Token invalide ou expiré');
          }
        }
        
        hasMoreUsers.value = false;
      } finally {
        loading.value = false;
         
      }
    };

    // Fonction pour charger les statistiques globales
    const loadGlobalStats = async () => {
      statsLoading.value = true;
      statsError.value = null;
      
      try {
        
        
        // Récupérer le token depuis le localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          consoleError('❌ Données utilisateur non trouvées');
          statsLoading.value = false;
          return;
        }

        const userObj = JSON.parse(userData);
        if (!userObj.data?.token) {
          consoleError('❌ Token non trouvé');
          statsLoading.value = false;
          return;
        }
        
        const response = await axios({
          method: 'get',
          url: 'https://api.peelo.io/get/stats',
          params: { all_bots: true },  // Indicate we want stats for ALL bots
          headers: {
            'Authorization': `Bearer ${userObj.data.token}`
          }
        });
        
        const statsData = response.data;
        //
        
        if (statsData && statsData.stats) {
          // Stocker les statistiques globales dans une variable dédiée
          globalStats.value = {
            chatbotCount: statsData.stats.chatbotCount || 0,
            userCount: statsData.stats.userCount || 0,
            discussionCount: statsData.stats.discussionCount || 0
          };
          
          // Définir les valeurs d'affichage
          totalChatbots.value = globalStats.value.chatbotCount;
          totalUsers.value = globalStats.value.userCount;
          totalDiscussions.value = globalStats.value.discussionCount;
          
          // Marquer les statistiques comme chargées
          statsLoaded.value = true;
          
          
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des statistiques:', error.response?.data || error.message || error);
        statsError.value = 'Impossible de charger les statistiques globales';
      } finally {
        statsLoading.value = false;
      }
    };

    // Fonction pour recharger uniquement les statistiques globales
    const refreshGlobalStats = async () => {
      try {
        
        await loadGlobalStats();
        
      } catch (error) {
        console.error('❌ Erreur lors du rafraîchissement des statistiques:', error);
      }
    };

    // Fonction pour charger plus d'utilisateurs (appelée par le bouton)
    const loadMoreUsers = () => {
      
      loadUsers(false);
    };

    // Fonction pour rechercher des utilisateurs
    const searchUsers = () => {
      
      // Réinitialiser les variables de pagination et recharger les utilisateurs
      page.value = 1;
      hasMoreUsers.value = true;
      loadUsers(true);
    };

    // Fonction pour débouncer la recherche
    const debounceSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      
      searchTimeout.value = setTimeout(() => {
        if (searchQuery.value.trim() !== '') {
          
          searchUsers();
        } else {
          
          page.value = 1;
          loadUsers(true);
        }
      }, 500); // Attendre 500ms avant de lancer la recherche
    };

    // Fonction pour actualiser la liste des utilisateurs
    const refreshUsers = () => {
      loadUsers(true);
    };

    // Formater les numéros de téléphone en fonction du pays
    const formatPhoneNumber = (phone) => {
      if (!phone) return '';
      
      // Nettoyage du numéro (suppression des caractères non numériques)
      const cleanNumber = phone.replace(/\D/g, '');
      
      // Si le numéro est vide après nettoyage
      if (!cleanNumber) return phone;
      
      // Définition des formats par région
      const phoneFormats = {
        // Europe
        '33': { // France
          format: (num) => num.startsWith('0') ? 
            num.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5') : 
            `+33 ${num.substring(2).replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}`,
          testLength: [10, 11, 12]
        },
        '44': { // Royaume-Uni
          format: (num) => `+44 ${num.substring(2).replace(/(\d{4})(\d{6})/, '$1 $2')}`,
          testLength: [12, 13]
        },
        '49': { // Allemagne
          format: (num) => `+49 ${num.substring(2).replace(/(\d{3})(\d{7})/, '$1 $2')}`,
          testLength: [12, 13]
        },
        '34': { // Espagne
          format: (num) => `+34 ${num.substring(2).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`,
          testLength: [11, 12]
        },
        '39': { // Italie
          format: (num) => `+39 ${num.substring(2).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`,
          testLength: [11, 12]
        },
        '351': { // Portugal
          format: (num) => `+351 ${num.substring(3).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`,
          testLength: [12, 13]
        },
        '32': { // Belgique
          format: (num) => `+32 ${num.substring(2).replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [11, 12]
        },
        '31': { // Pays-Bas
          format: (num) => `+31 ${num.substring(2).replace(/(\d{2})(\d{8})/, '$1 $2')}`,
          testLength: [12, 13]
        },
        '41': { // Suisse
          format: (num) => `+41 ${num.substring(2).replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [11, 12]
        },
        '43': { // Autriche
          format: (num) => `+43 ${num.substring(2).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`,
          testLength: [11, 12]
        },
        '46': { // Suède
          format: (num) => `+46 ${num.substring(2).replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [11, 12]
        },
        '47': { // Norvège
          format: (num) => `+47 ${num.substring(2).replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [10, 11]
        },
        '45': { // Danemark
          format: (num) => `+45 ${num.substring(2).replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [10, 11]
        },
        '358': { // Finlande
          format: (num) => `+358 ${num.substring(3).replace(/(\d{2})(\d{4})(\d{3})/, '$1 $2 $3')}`,
          testLength: [12, 13]
        },
        
        // Amérique du Nord
        '1': { // États-Unis/Canada
          format: (num) => num.length === 10 ? 
            `(${num.substring(0, 3)}) ${num.substring(3, 6)}-${num.substring(6)}` :
            `+1 ${num.substring(1).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}`,
          testLength: [10, 11]
        },
        
        // Amérique Latine
        '52': { // Mexique
          format: (num) => `+52 ${num.substring(2).replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3')}`,
          testLength: [12, 13]
        },
        '55': { // Brésil
          format: (num) => `+55 ${num.substring(2).replace(/(\d{2})(\d{4,5})(\d{4})/, '$1 $2-$3')}`,
          testLength: [12, 13]
        },
        
        // Afrique
        '212': { // Maroc
          format: (num) => `+212 ${num.substring(3).replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [12, 13]
        },
        '213': { // Algérie
          format: (num) => `+213 ${num.substring(3).replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [12, 13]
        },
        '216': { // Tunisie
          format: (num) => `+216 ${num.substring(3).replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')}`,
          testLength: [11, 12]
        },
        '20': { // Égypte
          format: (num) => `+20 ${num.substring(2).replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}`,
          testLength: [12, 13]
        },
        '27': { // Afrique du Sud
          format: (num) => `+27 ${num.substring(2).replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')}`,
          testLength: [11, 12]
        },
        '221': { // Sénégal
          format: (num) => `+221 ${num.substring(3).replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [12, 13]
        },
        '225': { // Côte d'Ivoire
          format: (num) => `+225 ${num.substring(3).replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [12, 13]
        },
        '237': { // Cameroun
          format: (num) => `+237 ${num.substring(3).replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')}`,
          testLength: [12, 13]
        },
        
        // Asie
        '86': { // Chine
          format: (num) => `+86 ${num.substring(2).replace(/(\d{3})(\d{8})/, '$1 $2')}`,
          testLength: [13, 14]
        },
        '91': { // Inde
          format: (num) => `+91 ${num.substring(2).replace(/(\d{5})(\d{5})/, '$1 $2')}`,
          testLength: [12, 13]
        },
        '81': { // Japon
          format: (num) => `+81 ${num.substring(2).replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3')}`,
          testLength: [12, 13]
        },
        '82': { // Corée du Sud
          format: (num) => `+82 ${num.substring(2).replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3')}`,
          testLength: [12, 13]
        },
        '65': { // Singapour
          format: (num) => `+65 ${num.substring(2).replace(/(\d{4})(\d{4})/, '$1 $2')}`,
          testLength: [10, 11]
        },
        
        // Océanie
        '61': { // Australie
          format: (num) => `+61 ${num.substring(2).replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3')}`,
          testLength: [12, 13]
        }
      };

      // Si le numéro commence par un 0 et a 10 chiffres, il s'agit probablement d'un numéro français
      if (cleanNumber.startsWith('0') && cleanNumber.length === 10) {
        return cleanNumber.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
      }
      
      // Tenter de déterminer le code pays
      let countryCode = null;
      // Essayer d'abord avec 3 chiffres, puis 2, puis 1
      if (cleanNumber.length >= 3) {
        const code3 = cleanNumber.substring(0, 3);
        if (phoneFormats[code3]) {
          countryCode = code3;
        }
      }
      
      if (!countryCode && cleanNumber.length >= 2) {
        const code2 = cleanNumber.substring(0, 2);
        if (phoneFormats[code2]) {
          countryCode = code2;
        }
      }
      
      if (!countryCode && cleanNumber.length >= 1) {
        const code1 = cleanNumber.substring(0, 1);
        if (phoneFormats[code1]) {
          countryCode = code1;
        }
      }
      
      // Si on a identifié un code pays et que le format correspondant existe
      if (countryCode && phoneFormats[countryCode]) {
        const format = phoneFormats[countryCode];
        
        // Vérifier que la longueur du numéro correspond à ce qu'on attend
        if (format.testLength.includes(cleanNumber.length)) {
          return format.format(cleanNumber);
        }
      }
      
      // Format générique pour les pays non spécifiés
      if (cleanNumber.length > 7) {
        // Essayons de deviner le code pays (généralement 1-3 chiffres)
        let countryCodeGuess = ''; 
        let nationalNumber = '';
        
        if (cleanNumber.length >= 11) {
          countryCodeGuess = cleanNumber.substring(0, 3);
          nationalNumber = cleanNumber.substring(3);
        } else if (cleanNumber.length >= 10) {
          countryCodeGuess = cleanNumber.substring(0, 2);
          nationalNumber = cleanNumber.substring(2);
        } else {
          countryCodeGuess = cleanNumber.substring(0, 1);
          nationalNumber = cleanNumber.substring(1);
        }
        
        // Format générique international: +XX XXXXXXXX
        return `+${countryCodeGuess} ${nationalNumber.replace(/(\d{2})(?=\d)/g, '$1 ')}`;
      }
      
      // Si on ne peut pas formatter, retourner tel quel mais avec des espaces tous les 2 chiffres
      return cleanNumber.replace(/(\d{2})(?=\d)/g, '$1 ');
    };

    // Formater les dates
    const formatDate = (dateString) => {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      
      // Si moins d'une journée, afficher en heures/minutes
      if (diffMs < 24 * 60 * 60 * 1000) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `Aujourd'hui à ${hours}:${minutes}`;
      }
      
      // Si moins d'une semaine, afficher le jour
      if (diffMs < 7 * 24 * 60 * 60 * 1000) {
        const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
        return `${days[date.getDay()]} à ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      }
      
      // Sinon, afficher la date complète
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // Formater les nombres avec des espaces
    const formatNumber = (number) => {
      if (number === undefined || number === null) return '0';
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    // Générer une URL d'avatar
    const getAvatarUrl = (name) => {
      if (!name) return `https://ui-avatars.com/api/?name=User&background=random`;
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff`;
    };

    // Fonctions pour les actions sur les utilisateurs
    const viewUserDetails = (user) => {
      
      activeActionMenu.value = null;
      router.push(`/dashboard/users/${user.id}`);
    };

    const editUser = (user) => {
      
      activeActionMenu.value = null;
      router.push(`/dashboard/users/${user.id}/edit`);
    };

    // Fonctionformat relatif des dates
    const formatTimeAgo = (dateString) => {
      if (!dateString) return 'Non spécifié';
      
      const date = new Date(dateString);
      
      // Tableau des mois en format court
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      
      // Récupérer les composants de la date
      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      // Retourner la date formatée
      return `${month} ${day}, ${year} - ${hours}:${minutes}`;
    };

    // Fonction pour rediriger vers la page chatbot
    const redirectToChatbotPage = (chatbotId) => {
      if (chatbotId) {
        
        // Utiliser le router pour naviguer vers la page du chatbot
        router.push(`/dashboard/chatbots/${chatbotId}/home`);
      } else {
        
        // Implémentation future: tri par chatbot lorsqu'on clique sur l'en-tête de colonne
      }
    };

    // Fonction pour basculer le tri des chatbots
    const toggleChatbotSort = () => {
      
      
      // Sauvegarder l'état original si ce n'est pas déjà fait
      if (!originalUsers.value.length) {
        originalUsers.value = [...users.value];
      }
      
      if (chatbotSort.value === 'none') {
        // Premier clic : trier par ordre alphabétique ascendant
        chatbotSort.value = 'asc';
        
        // Trier les utilisateurs par nom de chatbot
        const sortedUsers = [...originalUsers.value].sort((a, b) => {
          const botA = (a.bot || '').toLowerCase();
          const botB = (b.bot || '').toLowerCase();
          return botA.localeCompare(botB);
        });
        
        // Mettre à jour la liste des utilisateurs avec la version triée
        users.value = sortedUsers;
      } else {
        // Deuxième clic : revenir à l'ordre d'origine
        chatbotSort.value = 'none';
        users.value = [...originalUsers.value];
      }
    };

    // Fonction pour basculer le menu d'actions
    const toggleActionMenu = (index) => {
      if (activeActionMenu.value === index) {
        activeActionMenu.value = null;
      } else {
        activeActionMenu.value = index;
      }
    };

    // Fonction pour basculer le tri des activités
    const toggleActivitySort = () => {
      if (activitySort.value === 'none') {
        activitySort.value = 'desc';
        users.value = [...users.value].sort((a, b) => {
          const dateA = new Date(a.lastActivity || 0).getTime();
          const dateB = new Date(b.lastActivity || 0).getTime();
          return dateB - dateA;
        });
      } else if (activitySort.value === 'desc') {
        activitySort.value = 'asc';
        users.value = [...users.value].sort((a, b) => {
          const dateA = new Date(a.lastActivity || 0).getTime();
          const dateB = new Date(b.lastActivity || 0).getTime();
          return dateA - dateB;
        });
      } else {
        activitySort.value = 'none';
        // Restaurer l'ordre original
        users.value = [...originalUsers.value];
      }
      
      // Réinitialiser les autres tris
      interactionsSort.value = 'none';
      chatbotSort.value = 'none';
    };

    // Fonction pour ouvrir WhatsApp
    const openWhatsApp = (phone) => {
      if (!phone) return;
      
      // Nettoyer le numéro de téléphone (enlever les espaces et caractères spéciaux)
      const cleanPhone = phone.replace(/\D/g, '');
      
      // Construire l'URL WhatsApp
      const whatsappUrl = `https://wa.me/${cleanPhone}`;
      
      // Ouvrir dans un nouvel onglet
      window.open(whatsappUrl, '_blank');
    };

    // Fonction pour ouvrir la conversation
    const openConversation = (user) => {
      
      router.push(`/dashboard/chat/${user.idbot}/${user.phone}`);
    };

    // Monter le composant
    onMounted(async () => {
      
      // Charger les statistiques globales en premier
      await loadGlobalStats();
      
      // Charger les utilisateurs initiaux
     await loadUsers(true);

      // Ajouter l'écouteur de clic global
      document.addEventListener('click', handleGlobalClick);
    });

    // Nettoyer les écouteurs d'événements
    onUnmounted(() => {
      document.removeEventListener('click', handleGlobalClick);
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    const handleLimitChange = () => {
      
      page.value = 1;
      users.value = [];
      loadUsers(true);
    };

    const loadAllUsers = async () => {
      
      loading.value = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          console.error('❌ Données utilisateur non trouvées');
          return;
        }

        const userObj = JSON.parse(userData);
        if (!userObj.data?.token) {
          console.error('❌ Token non trouvé');
          return;
        }

        const response = await axios({
          method: 'get',
          url: `${apiBaseUrl}/get/strong/users`,
          params: {
            page: 1,
            limit: 20 // Un grand nombre pour obtenir tous les utilisateurs
          },
          headers: {
            'Authorization': `Bearer ${userObj.data.token}`
          }
        });

        if (response.data && response.data.users) {
          const formattedUsers = response.data.users.map(user => ({
            id: user.id_dialog ,
            iduser: user.iduser,
            name: user.user_full_name || 'Sans nom',
            phone:  user.tel  ,
            phonebot: user.telbot ,
            email: user.email || '',
            bot: user.bot_name || 'Non spécifié',
            idbot: user.idbot || null,
            discussions: user.total_discussions || 0,
            lastActivity: user.created_at || new Date().toISOString(),
            isActive: user.is_active || true,
            profileImage: user.user_image_profile || ''
          }));

          users.value = formattedUsers;
          originalUsers.value = formattedUsers;
          hasMoreUsers.value = false;
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement de tous les utilisateurs:', error);
      } finally {
        loading.value = false;
      }
    };

    // Fonction pour basculer le tri des interactions
    const toggleInteractionsSort = () => {
      if (interactionsSort.value === 'none') {
        interactionsSort.value = 'desc';
        users.value = [...users.value].sort((a, b) => (b.discussions || 0) - (a.discussions || 0));
      } else if (interactionsSort.value === 'desc') {
        interactionsSort.value = 'asc';
        users.value = [...users.value].sort((a, b) => (a.discussions || 0) - (b.discussions || 0));
      } else {
        interactionsSort.value = 'none';
        // Restaurer l'ordre original
        users.value = [...originalUsers.value];
      }
      
      // Réinitialiser les autres tris
      activitySort.value = 'none';
      chatbotSort.value = 'none';
    };

    return {
      users,
      loading,
      hasMoreUsers,
      scrollContainer,
      tableBody,
      searchQuery,
      totalUsers,
      totalDiscussions,
      totalChatbots,
      statsLoading,
      statsError,
      globalStats,
      chatbotSort,
      toggleChatbotSort,
      loadUsers,
      loadMoreUsers,
      loadGlobalStats,
      refreshGlobalStats,
      debounceSearch,
      refreshUsers,
      formatPhoneNumber,
      formatDate,
      formatNumber,
      getAvatarUrl,
      viewUserDetails,
      editUser,
      formatTimeAgo,
      redirectToChatbotPage,
      activeActionMenu,
      toggleActionMenu,
      activitySort,
      toggleActivitySort,
      handleLimitChange,
      loadAllUsers,
      selectedLimit,
      openWhatsApp,
      interactionsSort,
      toggleInteractionsSort,
      showFilterMenu,
      filters,
      activeFilters,
      toggleFilterMenu,
      applyFilters,
      clearFilters,
      openConversation
    };
  }
};
</script>

<style scoped>
/* Couleurs WhatsApp */
:root {
  --whatsapp-teal: #25D366;
  --whatsapp-green: #128C7E;
  --whatsapp-light-green: #dcf8c6;
  --whatsapp-blue: #34B7F1;
  --whatsapp-dark: #075E54;
}

.all-users-container {
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  height: 100%;
  width: 100%;
  max-width: 100%;
  background-color: var(--bg-color);
  color: #37352f;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

.users-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin: 16px 0px 32px;
  width: 100%;
  box-sizing: border-box;
  padding: 0 32px;
}

/* Nouvelles cartes de statistiques inspirées de DashboardStats */
.stats-card {
  background: linear-gradient(135deg, var(--whatsapp-green), var(--whatsapp-dark));
  border-radius: 16px;
  padding: 20px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

.stats-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.15), transparent 80%);
  z-index: 0;
}

/* Nouveaux patterns pour les cartes de statistiques */
.stats-card:nth-child(1) {
  background: linear-gradient(135deg, #128C7E, #075E54);
}

.stats-card:nth-child(1)::after {
  content: '';
  position: absolute;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  top: -70px;
  right: -70px;
  z-index: 0;
}

.stats-card:nth-child(2) {
  background: linear-gradient(135deg, #34B7F1, #0277BD);
}

.stats-card:nth-child(2)::after {
  content: '';
  position: absolute;
  width: 120px;
  height: 120px;
  border: 15px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  bottom: -80px;
  left: -60px;
  z-index: 0;
}

.stats-card:nth-child(3) {
  background: linear-gradient(135deg, #2ecc71, #27ae60);
}

.stats-card:nth-child(3)::after {
  content: '';
  position: absolute;
  width: 100px;
  height: 200px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transform: rotate(-45deg);
  top: -50px;
  right: -20px;
  z-index: 0;
}

/* Animation de pulsation pour les cercles icons */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

.circle-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  animation: pulse 2s infinite;
}

.stats-card:nth-child(1) .circle-icon {
  background: rgba(255, 255, 255, 0.25);
  animation-delay: 0s;
}

.stats-card:nth-child(2) .circle-icon {
  background: rgba(255, 255, 255, 0.25);
  animation-delay: 0.5s;
}

.stats-card:nth-child(3) .circle-icon {
  background: rgba(255, 255, 255, 0.25);
  animation-delay: 1s;
}

.circle-icon svg {
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
}

.stats-info {
  flex: 1;
  z-index: 1;
}

.stats-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.stats-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.stats-value .number {
  font-size: 2.2rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.5px;
}

.refresh-stats {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  opacity: 0;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.stats-card:hover .refresh-stats {
  opacity: 1;
}

.refresh-stats:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(30deg);
}

.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  padding: 0 32px 16px;
  width: 100%;
  box-sizing: border-box;
}

.search-container {
  position: relative;
  width: 320px;
  transition: all 0.3s ease;
}

.search-container input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  background: #f0f0f0;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  color: #37352f;
}

.search-container input::placeholder {
  color: #37352f;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.search-container input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.search-container input:focus::placeholder {
  opacity: 0.4;
  transform: translateX(4px);
}

.search-container svg {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  color: #37352f;
  opacity: 0.6;
  transition: all 0.3s ease;
  width: 18px;
  height: 18px;
}

.search-container input:focus + svg {
  opacity: 0.8;
  transform: translateY(-50%) scale(1.1);
}

/* Animation de focus */
@keyframes searchFocus {
  0% {
    box-shadow: 0 0 0 0 rgba(18, 140, 126, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(18, 140, 126, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(18, 140, 126, 0);
  }
}

.search-container input:focus {
  animation: searchFocus 1s ease-out;
}

/* Style pour le mode sombre */
@media (prefers-color-scheme: dark) {
  .search-container input {
    background: #ffffff;
    border-color: #128C7E;
    color: #075E54;
  }

  .search-container input::placeholder {
    color: #128C7E;
    opacity: 0.7;
  }

  .search-container svg {
    color: #128C7E;
  }

  .search-container input:focus {
    border-color: #25D366;
    background: #ffffff;
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.15);
  }
}

.table-actions {
  display: flex;
  gap: 12px;
  position: relative;
}

.filter-button {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  font-size: 14px;
  color: #37352f;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-button:hover {
  background: #f7f7f7;
  border-color: var(--whatsapp-green);
}

.filter-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--whatsapp-green);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  min-width: 240px;
  z-index: 1000;
  border: 1px solid #e0e0e0;
}

.filter-section {
  margin-bottom: 16px;
}

.filter-section:last-child {
  margin-bottom: 0;
}

.filter-section h4 {
  font-size: 14px;
  color: #666;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  cursor: pointer;
  font-size: 14px;
  color: #37352f;
  transition: all 0.2s;
}

.filter-option:hover {
  color: var(--whatsapp-green);
}

.filter-option input[type="checkbox"] {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  accent-color: var(--whatsapp-green);
}

.filter-actions {
  border-top: 1px solid #f0f0f0;
  padding-top: 16px;
  margin-top: 16px;
}

.clear-filters {
  width: 100%;
  padding: 8px;
  background: #f5f5f5;
  border: none;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-filters:hover {
  background: #e0e0e0;
  color: #37352f;
}

.add-user-button {
  background: var(--whatsapp-green);
  color: white;
  border: none;
}

.add-user-button:hover {
  background: var(--whatsapp-dark);
}

.users-list-container {
  flex: 1;
  margin: 0;
  padding: 0 32px 32px;
  width: 100%;
  box-sizing: border-box;
  position: relative;
}

.users-table {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  font-size: 14px;
}

thead {
  background: transparent;
  position: sticky;
  top: 0;
  z-index: 10;
}

tbody {
  display: block;
  max-height: calc(15 * 72px); /* 72px est la hauteur approximative d'une ligne (padding + contenu) */
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(55, 53, 47, 0.3) transparent;
}

tbody::-webkit-scrollbar {
  width: 6px;
}

tbody::-webkit-scrollbar-track {
  background: transparent;
}

tbody::-webkit-scrollbar-thumb {
  background-color: rgba(55, 53, 47, 0.3);
  border-radius: 3px;
}

thead, tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

/* Définition des largeurs de colonnes */
th, td {
  padding: 16px;
  text-align: left;
}

/* Colonne utilisateur */
th:nth-child(1), td:nth-child(1) {
  width: 20%;
}

/* Colonne chatbot */
th:nth-child(2), td:nth-child(2) {
  width: 15%;
}

/* Colonne interactions */
th:nth-child(3), td:nth-child(3) {
  width: 15%;
  text-align: center;
}

/* Colonne dernière activité */
th:nth-child(4), td:nth-child(4) {
  width: 23%;
}

/* Colonne actions */
th:nth-child(5), td:nth-child(5) {
   
  text-align: center;
}

tbody tr {
  transition: all 0.3s ease;
  margin-bottom: 8px;
}

tbody td {
  padding: 16px;
  border: none;
  color: #37352f;
  background: #f0f0f0;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  height: 100%;
  vertical-align: middle;
}

tbody tr:hover td {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transform: translateY(-2px);
 }

tbody tr td:first-child {
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

tbody tr td:last-child {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
}

.checkbox-cell {
  width: 40px;
  text-align: center;
}

.checkbox {
  width: 16px;
  height: 16px;
  border: 1px solid #ccc;
  border-radius: 3px;
  cursor: pointer;
}

.user-cell {
  min-width: 240px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.status-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #d1d5db;
  border: 2px solid white;
}

.status-indicator.active {
  background-color: var(--whatsapp-teal);
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-weight: 500;
  color: #37352f;
}

.user-email {
  font-size: 12px;
  color: #888;
}

.access-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.access-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: #f0f0f0;
  color: #666;
}

.access-tag.admin {
  background: rgba(18, 140, 126, 0.1);
  color: #128C7E;
}

.access-tag.data {
  background: #128C7E;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 13px;
}

.access-tag.data:hover {
  background: #075E54;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(7, 94, 84, 0.2);
}

.actions-cell {
  width: 60px;
  text-align: center;
}

.table-action-button {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  cursor: pointer;
}

.table-action-button:hover {
  background: #f0f0f0;
  color: #37352f;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
}

.pagination-info {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 14px;
  color: #666;
}

.users-per-page {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.users-per-page::before {
  content: 'Afficher';
  color: #666;
  font-size: 14px;
}

.users-per-page select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 6px 32px 6px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: white;
  color: #37352f;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 12px;
}

.users-per-page select:hover {
  border-color: #25D366;
  background-color: #f8f8f8;
}

.users-per-page select:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 2px rgba(37, 211, 102, 0.1);
}

.users-per-page select option {
  padding: 8px;
  background-color: white;
  color: #37352f;
}

.users-per-page::after {
  content: 'utilisateurs';
  color: #666;
  font-size: 14px;
  margin-left: 4px;
}

.load-more-container {
  display: flex;
  justify-content: center;
}

.load-more-button {
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #37352f;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-button:hover {
  background: #f7f7f7;
}

.loading-container, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #888;
}

.loader {
  width: 32px;
  height: 32px;
  border: 2px solid rgba(18, 140, 126, 0.1);
  border-radius: 50%;
  border-top-color: var(--whatsapp-green);
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state svg {
  color: #ccc;
  margin-bottom: 16px;
}

.action-button {
  background: var(--whatsapp-green);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  margin-top: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-button:hover {
  background: var(--whatsapp-dark);
}

.loading-more {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  color: #888;
}

.loading-more .loader {
  width: 24px;
  height: 24px;
  margin-bottom: 8px;
}

.end-message {
  padding: 16px;
  color: #888;
  text-align: center;
}

.chatbot-header {
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.chatbot-header:hover {
  color: var(--whatsapp-green);
}

.chatbot-header svg {
  transition: transform 0.2s, color 0.2s;
}

.chatbot-header:hover svg {
  transform: scale(1.1);
}

.sort-active {
  color: var(--whatsapp-green);
}

.interactions-header {
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  width: 100%;
}

.interactions-header:hover {
  color: var(--whatsapp-green);
}

.interactions-header svg {
  transition: transform 0.2s, color 0.2s;
  margin-left: 4px;
}

.interactions-header:hover svg {
  transform: scale(1.1);
}

.interactions-cell {
  text-align: center;
  width: 120px;
}

.interactions-count {
  font-weight: 500;
  color: #37352f;
  background: rgba(37, 211, 102, 0.15);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  padding: 6px 12px;
  border-radius: 8px;
  display: inline-block;
  min-width: 40px;
  text-align: center;
  transition: all 0.3s ease;
}

/* Responsive */
@media (max-width: 1024px) {
  .users-stats {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .users-header, .page-title-section, .users-stats, .table-controls {
    padding-left: 16px;
    padding-right: 16px;
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
  
  .users-list-container {
    margin: 0;
    padding: 0 16px 16px;
    width: 100%;
  }
  
  .breadcrumb {
    display: none;
  }
}

/* Styles pour le menu d'actions */
.actions-dropdown {
  position: relative;
}

.action-menu {
  position: absolute;
  right: 0;
  top: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 100;
  overflow: hidden;
  margin-top: 4px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: none;
  color: #37352f;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.action-item:hover {
  background: #f5f5f5;
  color: var(--whatsapp-green);
}

.action-item svg {
  color: #666;
}

.action-item:hover svg {
  color: var(--whatsapp-green);
}

.action-icons {
  display: flex;
  gap: 12px;
  justify-content: center;
  height: 100%;
  align-items: center;
  min-height: 40px; /* Pour garantir une hauteur minimale cohérente */
  padding-bottom: 35px;
  padding-top: 25px;
}

.action-icon-container {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.action-icon-container:hover {
  background-color: #25D366;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
}

.action-icon-container svg {
  transition: all 0.2s ease;
}

.action-icon-container:hover svg {
  transform: scale(1.1);
  stroke: white;
}

/* Tooltip styles */
.action-icon-container::after {
  content: attr(title);
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(37, 211, 102, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
  z-index: 100;
}

.action-icon-container:hover::after {
  opacity: 1;
}

@media (max-width: 900px) {
  .desktop-only {
    display: none !important;
  }
  
  .mobile-only {
    display: flex !important;
  }
  
  .mobile-users-grid {
    flex-direction: column;
    gap: 16px;
    padding-bottom: 80px; 
  }
}

@media (min-width: 901px) {
   .mobile-only {
       display: none !important;
   }
   
   .desktop-only {
       display: block !important;
   }
}
</style> 